import { type MouseEvent, type ReactNode, useState } from 'react';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CardMenuButton, { cardWithMenuSx } from './CardMenuButton';

export interface MediaCardProps {
  content: string | ReactNode;
  imageSrc?: string | null;
  imageLoading?: 'lazy' | 'eager'; // Defaults to lazy; use eager for above-the-fold or preloaded assets.
  aspectRatio?: number; // Width / height of the image. Reserves the image height before it loads and fades the image in.
  fetchPriority?: 'high' | 'low' | 'auto';
  selected?: boolean; // Outlines the card in the primary color.
  fullHeight?: boolean; // Stretch to the parent's height, e.g. to match row heights in a CSS grid.
  onClick: () => void;
  onMenuClick?: (event: MouseEvent<HTMLElement>) => void;
}

const MediaCard = ({
  content,
  imageSrc,
  imageLoading = 'lazy',
  aspectRatio,
  fetchPriority,
  selected,
  fullHeight = false,
  onClick,
  onMenuClick,
}: MediaCardProps) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const alt = typeof content === 'string' ? content : '';

  const renderImage = () => {
    if (!imageSrc || (aspectRatio != null && failedSrc === imageSrc)) {
      return (
        <Box
          sx={{
            minHeight: 100,
            aspectRatio,
            bgcolor: 'action.hover',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <ImageOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
        </Box>
      );
    }

    if (aspectRatio == null) {
      return (
        <CardMedia
          component="img"
          image={imageSrc}
          alt={alt}
          loading={imageLoading}
          fetchPriority={fetchPriority}
          decoding="async"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
      );
    }

    return (
      <Box sx={{ position: 'relative', aspectRatio, bgcolor: 'action.hover' }}>
        <CardMedia
          component="img"
          image={imageSrc}
          alt={alt}
          loading={imageLoading}
          fetchPriority={fetchPriority}
          decoding="async"
          onLoad={() => setLoadedSrc(imageSrc)}
          onError={() => setFailedSrc(imageSrc)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loadedSrc === imageSrc ? 1 : 0,
            transition: (theme) => theme.transitions.create('opacity'),
          }}
        />
      </Box>
    );
  };

  return (
    <Card
      variant="outlined"
      elevation={0}
      sx={{
        ...cardWithMenuSx,
        overflow: 'hidden',
        ...(fullHeight && { height: '100%' }),
        ...(selected && {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        }),
      }}
    >
      {onMenuClick ? (
        <CardMenuButton variant="filled" onClick={onMenuClick} />
      ) : null}
      <CardActionArea
        onClick={onClick}
        sx={{
          ...(fullHeight && {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
          }),
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 1,
            backgroundColor: (theme) =>
              alpha(
                theme.palette.mode === 'dark'
                  ? theme.palette.common.white
                  : theme.palette.common.black,
                0.2
              ),
          },
        }}
      >
        {renderImage()}
        <CardContent sx={fullHeight ? { flexGrow: 1 } : undefined}>
          {typeof content === 'string' ? (
            <Typography
              variant="subtitle2"
              textAlign="center"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {content}
            </Typography>
          ) : (
            content
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default MediaCard;
